import { pseBankNameFor, usePseBanks } from "@/modules/dashboard/payins/hooks/usePseBanks";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";

import type { PsePayerValues } from "./psePayer.schema";
import { PsePayerFields } from "./PsePayerFields";
import { type PsePayerDefaults, usePsePayerForm } from "./usePsePayerForm";

export interface PsePayerStepProps {
  /**
   * HTML id of the form. The host renders the submit button in its own footer and wires it with
   * `form={formId}`, so the action row can live in a dialog footer or a page card without being duplicated.
   */
  formId: string;
  /** Values to restore when the step is re-entered from the confirmation screen. */
  defaults: PsePayerDefaults;
  reference: string;
  /** Amount already formatted for display, or `null` until the operation has been priced. */
  amount: string | null;
  currency: string;
  /** Fires only with a valid payer. `bankName` is resolved here so the host never re-reads the directory. */
  onSubmit: (values: PsePayerValues, meta: { bankName: string }) => void;
}

/**
 * The PSE payer step, complete: schema, the person-type rule, the bank directory and the redesigned fields.
 *
 * Both entry points — `/app/payins` (Colombia tab) and the COP wallet deposit modal — render exactly this, so
 * a change to what the network requires lands on both at once. Only the surrounding chrome differs, and that
 * is why the submit button is the host's: a dialog puts it in a sticky footer, a page puts it under the
 * card.
 */
export function PsePayerStep({ formId, defaults, reference, amount, currency, onSubmit }: PsePayerStepProps) {
  const form = usePsePayerForm(defaults);
  const { banks, error: banksError, isLoading: banksLoading, retry: onRetryBanks } = usePseBanks(true);

  return (
    <ColocaForm
      id={formId}
      form={form}
      className="space-y-0"
      onSubmit={(values) => onSubmit(values, { bankName: pseBankNameFor(banks, values.bankCode) })}
    >
      <PsePayerFields
        reference={reference}
        amount={amount}
        currency={currency}
        banks={banks}
        banksError={banksError}
        banksLoading={banksLoading}
        onRetryBanks={onRetryBanks}
      />
    </ColocaForm>
  );
}
